
import { defineComponent, computed, ref, SetupContext, onMounted, onUnmounted, CSSProperties } from 'vue';
import TCopyright from './../t-copyright/index';
import TPortrait from './../t-portrait/index';
import TSlogan from './../t-slogan/index';

export default defineComponent({
  name: 'TLayout',
  components: { TCopyright, TPortrait, TSlogan },
  props: {
    isFixed: { type: Boolean, default: true },
    isFooterPadding: { type: Boolean, default: true },
    isFooterBorder: { type: Boolean, default: false },
    isCopyright: { type: Boolean, default: true },
    isSlogan: { type: Boolean, default: false },
    isIntroduction: { type: Boolean, default: false },
    backgroundColor: { type: String, default: '' },
    headerBackgroundColor: { type: String, default: '' },
    footerBackgroundColor: { type: String, default: '' },
    /** 是否需要横坚屏切换提示 */
    isOrientationChange: { type: Boolean, default: true },
    /** 文案的多语言配置信息 */
    lang: {
      type: String,
      default: 'zh-HK',
      validator: (value: string) => ['en-GB', 'zh-CN', 'zh-HK'].includes(value),
    },
    /**
     * 主题配置信息
     * 如果配置了dark模式的话，则全屏会自动切换至处暗黑模式
     * 包括依赖的一些组件(注意：因为业务迭代的原因，并不会全部组件都会自动切换的)
     */
    theme: {
      type: String,
      default: 'highlight',
      validator: (value: string) => ['dark', 'highlight'].includes(value),
    },
  },
  emits: ['click', 'scroll', 'loadmore'],
  setup(props, context: SetupContext) {
    /** 组件的className前辍 */
    const prefixCls = ref('t-layout');

    /** 组件的className前辍 */
    const getClasses = computed(() => {
      return {
        wrap: [prefixCls.value, `${prefixCls.value}__${props.theme}`],
        header: [`${prefixCls.value}-hd`],
        body: [`${prefixCls.value}-bd`],
        footer: [`${prefixCls.value}-ft`, props.isFixed && `${prefixCls.value}-ft__fixed`],
        footerBody: [
          context.slots.footer && props.isFooterBorder && `${prefixCls.value}-ft__border`,
          context.slots.footer && props.isFooterPadding && props.isCopyright && `${prefixCls.value}-ft__copyright`,
          context.slots.footer &&
            props.isFooterPadding &&
            (!props.isCopyright || props.isIntroduction) &&
            `${prefixCls.value}-ft__padding`,
        ],
      };
    });
    /** 组件背景 */
    const styles = computed(() => {
      if (props.backgroundColor) {
        return {
          'background-color': props.backgroundColor,
        } as CSSProperties;
      }
      return '';
    });

    const headerStyles = computed(() => {
      if (props.headerBackgroundColor) {
        return {
          'background-color': props.headerBackgroundColor,
        } as CSSProperties;
      }
      return '';
    });
    const footerStyles = computed(() => {
      if (props.footerBackgroundColor) {
        return {
          'background-color': props.footerBackgroundColor,
        } as CSSProperties;
      }
      return '';
    });

    /** 对外响应的事件 */
    const listener = (event: any) => context.emit('click', event);

    /** 让页面滚动到指定的地方 */
    const onScrollTo = (pos: number) => {
      const html: HTMLElement | null = document.documentElement;
      if (html) {
        html.scrollTop = pos;
      }
    };

    /** 滚动事件 */
    let beforeScroll = 0;
    const onScroll = (event: any) => {
      context.emit('scroll', event);
      const { clientHeight, scrollHeight } = event.target.documentElement;
      const tempScrollTop = (
        event.target.documentElement.scrollTop ||
        document.body.scrollTop ||
        window.pageYOffset
      ).toFixed(0);
      const scrollTop = parseInt(tempScrollTop, 10);
      const diff = scrollTop - beforeScroll;
      const type = diff > 0 ? 'down' : 'up';
      beforeScroll = scrollTop;
      if (type === 'down' && clientHeight + scrollTop >= scrollHeight) {
        context.emit('loadmore', event);
      }
    };

    onMounted(() => {
      document.addEventListener('scroll', onScroll, false);
    });

    onUnmounted(() => {
      document.removeEventListener('scroll', onScroll, false);
    });

    const onTapByPlaceholder = () => {
      return;
    };

    return {
      prefixCls,
      getClasses,
      styles,
      headerStyles,
      footerStyles,
      listener,
      onScroll,
      onScrollTo,
      onTapByPlaceholder,
    };
  },
});
